import React from "react";
import {
  fetchPullPlanScopeMembers,
  fetchPullPlanTaktSections,
  fetchPullPlanWBSTree,
  getPullPlan,
  listPullPlans,
} from "../../../../api/clientV2";
import type { PullPlanKind, PullPlanWBSNode } from "../../../../models";
import Spinner from "../../../../components/Spinner/Spinner";
import { apiErrorMessage } from "../../../../utils/apiError";
import {
  draftNamesByWbs,
  leafFactsByWbs,
  leafIdSet,
  leafSectionTag,
  scopeIdsFromPayload,
  visibleNodes,
  type LeafFacts,
} from "./helpers";
import { createFromWbs } from "./createPlan";
import "./NewPlanFromWbsPanel.css";

interface Props {
  scheduleId: number;
  onCancel: () => void;
  /** The plan was created — route into the workspace. `anchorWbsId` is the
   *  first checked leaf (the draft's section_wbs_id anchor); `kind` decides
   *  whether the host opens it as a Takt section or a plain WBS plan. */
  onCreated: (args: {
    sectionName: string;
    draftId: number;
    anchorWbsId: number;
    kind: PullPlanKind;
  }) => void;
}

const NewPlanFromWbsPanel: React.FC<Props> = ({ scheduleId, onCancel, onCreated }) => {
  const [tree, setTree] = React.useState<PullPlanWBSNode[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [collapsed, setCollapsed] = React.useState<Set<number>>(() => new Set());
  // Check order matters: the first checked leaf pre-fills the name and anchors
  // the draft row.
  const [checkedOrder, setCheckedOrder] = React.useState<number[]>([]);
  // Every WBS plan is a Takt-scoped section that loads all its chosen leaves.
  const [kind] = React.useState<PullPlanKind>("takt");
  const [name, setName] = React.useState("");
  const [nameTouched, setNameTouched] = React.useState(false);
  const [facts, setFacts] = React.useState<Map<number, LeafFacts>>(() => new Map());
  const [factsLoading, setFactsLoading] = React.useState(false);
  // Which Takt section(s) already own each leaf — shown up front on every row.
  const [sectionByWbs, setSectionByWbs] = React.useState<Map<number, string[]>>(
    () => new Map(),
  );
  // wbs leaf id → names of other ACTIVE drafts already scoping it.
  const [draftsByWbs, setDraftsByWbs] = React.useState<Map<number, string[]>>(
    () => new Map(),
  );
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchPullPlanWBSTree(scheduleId)
      .then((nodes) => {
        if (!cancelled) setTree(nodes);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message ?? "Failed to load the WBS tree.");
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  React.useEffect(() => {
    let live = true;
    fetchPullPlanTaktSections(scheduleId)
      .then((rows) => {
        if (!live) return;
        const m = new Map<number, string[]>();
        for (const s of rows) {
          for (const n of s.wbs_nodes ?? []) {
            const list = m.get(n.id);
            if (list) list.push(s.name);
            else m.set(n.id, [s.name]);
          }
        }
        setSectionByWbs(m);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [scheduleId]);

  // Collision facts: every active draft's explicit scope, fetched once.
  React.useEffect(() => {
    let cancelled = false;
    listPullPlans(scheduleId, { status: "draft" })
      .then((drafts) =>
        Promise.all(
          drafts.map((d) =>
            getPullPlan(scheduleId, d.id).then((full) => ({
              name: d.name,
              scopeWbsIds: scopeIdsFromPayload(full.payload_json),
            })),
          ),
        ),
      )
      .then((scopes) => {
        if (!cancelled) setDraftsByWbs(draftNamesByWbs(scopes));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  const checkedSet = React.useMemo(() => new Set(checkedOrder), [checkedOrder]);

  // Per-leaf facts for the checked set, debounced behind check churn.
  React.useEffect(() => {
    if (checkedOrder.length === 0) {
      setFacts(new Map());
      setFactsLoading(false);
      return;
    }
    let cancelled = false;
    setFactsLoading(true);
    const handle = window.setTimeout(() => {
      fetchPullPlanScopeMembers(scheduleId, checkedOrder)
        .then((members) => {
          if (cancelled) return;
          setFacts(leafFactsByWbs(members));
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setFactsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [scheduleId, checkedOrder]);

  const leaves = React.useMemo(() => (tree ? leafIdSet(tree) : new Set<number>()), [tree]);
  const nodeById = React.useMemo(() => {
    const m = new Map<number, PullPlanWBSNode>();
    if (tree) for (const n of tree) m.set(n.id, n);
    return m;
  }, [tree]);
  const rows = React.useMemo(
    () => (tree ? visibleNodes(tree, collapsed) : []),
    [tree, collapsed],
  );

  const totalActivities = React.useMemo(() => {
    let total = 0;
    for (const id of checkedOrder) total += facts.get(id)?.total ?? 0;
    return total;
  }, [checkedOrder, facts]);

  const toggleLeaf = (id: number) => {
    setCreateError(null);
    setCheckedOrder((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!nameTouched) {
        const first = next.length > 0 ? nodeById.get(next[0]) : null;
        setName(first ? first.name : "");
      }
      return next;
    });
  };

  const toggleBranch = (id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const trimmedName = name.trim();
  const canCreate = checkedOrder.length > 0 && trimmedName.length > 0 && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    setCreateError(null);
    try {
      const leafNameById = new Map<number, string>();
      for (const id of checkedOrder) {
        const n = nodeById.get(id);
        if (n) leafNameById.set(id, n.name);
      }
      const result = await createFromWbs(scheduleId, {
        kind,
        leafIds: checkedOrder,
        name: trimmedName,
        leafNameById,
      });
      onCreated(result);
    } catch (e: unknown) {
      setCreateError(apiErrorMessage(e, "Failed to create the plan."));
      setCreating(false);
    }
  };

  return (
    <div className="new-plan-panel">
      <div className="new-plan-panel-head">
        <h3 className="new-plan-panel-title">New plan</h3>
        <span className="new-plan-panel-tag">
          Check the levels that share the recurring work you want to plan
          together, name it, and create.
        </span>
      </div>

      <div className="new-plan-panel-body">
        <div className="new-plan-panel-tree-col">
          {loadError && <div className="pull-plan-error">{loadError}</div>}
          <div className="new-plan-panel-tree">
            <Spinner
              visible={tree === null && !loadError}
              label="Loading WBS tree..."
            />
            {rows.map((n) => {
              const isLeaf = leaves.has(n.id);
              const isChecked = checkedSet.has(n.id);
              const tag = leafSectionTag(sectionByWbs.get(n.id));
              const overlapNames = draftsByWbs.get(n.id);
              return (
                <div
                  key={n.id}
                  className={`new-plan-node${isLeaf ? " is-leaf" : ""}${isChecked ? " is-checked" : ""}`}
                  style={{ paddingLeft: `${n.depth * 16}px` }}
                >
                  {isLeaf ? (
                    <label className="new-plan-leaf-row">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleLeaf(n.id)}
                        disabled={creating}
                      />
                      <span className="new-plan-leaf-name">{n.name}</span>
                      <span
                        className={`new-plan-sec-chip new-plan-sec-chip--${tag.kind}`}
                        title={
                          tag.kind === "none"
                            ? "No Takt section — free to claim"
                            : `Tied to ${(sectionByWbs.get(n.id) ?? []).join(", ")}`
                        }
                      >
                        {tag.label}
                      </span>
                      <span className="new-plan-leaf-count">{n.counts.tasks}</span>
                      {overlapNames && overlapNames.length > 0 && !isChecked && (
                        <span
                          className="new-plan-overlap-dot"
                          title={`Already in scope of ${overlapNames.join(", ")}`}
                        />
                      )}
                    </label>
                  ) : (
                    <button
                      type="button"
                      className="new-plan-branch-row"
                      onClick={() => toggleBranch(n.id)}
                    >
                      <span className="new-plan-caret" aria-hidden="true">
                        {collapsed.has(n.id) ? "▸" : "▾"}
                      </span>
                      <strong>{n.name}</strong>
                      {n.counts.tasks > 0 && (
                        <span className="new-plan-branch-count">{n.counts.tasks}</span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="new-plan-summary">
            {checkedOrder.length} {checkedOrder.length === 1 ? "leaf" : "leaves"}{" "}
            selected
            {checkedOrder.length > 0 && (
              <>
                {" · "}
                <b>
                  {factsLoading ? "…" : totalActivities} activit
                  {totalActivities === 1 ? "y" : "ies"}
                </b>
              </>
            )}
            . The section tags show what each leaf is already tied to; untagged
            work joins when you create it.
          </p>
        </div>

        <div className="new-plan-choice-col">
          <div className="new-plan-kind-note">
            <div className="new-plan-kind-name">◧ Takt section</div>
            <div className="new-plan-kind-why">
              Your plan loads every level you checked on one board, shows on the
              Takt board, and lets you edit the recurring work across all of them
              at once.
            </div>
          </div>
          <input
            className="new-plan-name-input"
            type="text"
            value={name}
            placeholder="Name this plan / section"
            onChange={(e) => {
              setNameTouched(true);
              setName(e.target.value);
            }}
            disabled={creating}
          />
          {createError && <div className="pull-plan-error">{createError}</div>}
          <div className="new-plan-actions">
            <button
              type="button"
              className="new-plan-cancel"
              onClick={onCancel}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="new-plan-create"
              onClick={handleCreate}
              disabled={!canCreate}
              title={
                canCreate ? undefined : "Check at least one leaf and name the plan"
              }
            >
              {creating ? "Creating…" : "Create →"}
            </button>
          </div>
          <p className="new-plan-foot-note">It lands in your list; plan it from there.</p>
        </div>
      </div>
    </div>
  );
};

export default NewPlanFromWbsPanel;
